import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NEVER, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  first,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import * as URI from 'urijs';

import {
  AnonymousUser,
  AuthService,
  PersonaPkceAuthService,
  PersonaUser,
  createPersonaUserFromPkceLogin,
} from '®shared/auth';
import { AuthErrorService } from '®shared/auth/auth-error.service';
import { AppConfig } from '®shared/config/app-config';
import { RedirectionService } from '®shared/redirection';
import { UserService } from '®shared/user';

import {
  currentAnonymousUser,
  currentPersonaUser,
} from './current-user-and-permissions';
import {
  CompleteBreakoutLoginAction,
  CompleteLtiBreakoutLoginAction,
  CurrentUserActionTypes,
  LogInDuringRouteNavigationAction,
  StoreAccessTokenAction,
  StoreCurrentUserAction,
} from './current-user.actions';

@Injectable()
export class CurrentUserEffects {
  constructor(
    private actions$: Actions,
    private appConfig: AppConfig,
    private authService: AuthService,
    private authErrorService: AuthErrorService,
    private personaPkceService: PersonaPkceAuthService,
    private redirectionService: RedirectionService,
    private userService: UserService,
    private router: Router,
  ) {}

  establishCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentUserActionTypes.REQUEST_CURRENT_USER),
      first(),
      switchMap(() => this.authService.authorizeByUrl()),
      switchMap(oauthUser => {
        if (oauthUser.isAnonymous) {
          return of(
            currentAnonymousUser(this.appConfig, oauthUser as AnonymousUser),
          );
        }
        const personaUser = oauthUser as PersonaUser;
        return this.userService
          .loginWithTarl(personaUser.guid, personaUser.activePid)
          .pipe(
            catchError(() => of(null)),
            map(tarlUser =>
              currentPersonaUser(this.appConfig, personaUser, tarlUser),
            ),
          );
      }),
      map(currentUser => new StoreCurrentUserAction({ currentUser })),
    ),
  );

  completeRedirectLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentUserActionTypes.COMPLETE_REDIRECT_LOGIN),
      first(),
      switchMap(() => this.personaPkceService.completeRedirectAuthentication()),
      map(({ loginData, appState }) => {
        const personaUser = createPersonaUserFromPkceLogin(loginData);
        const isTalisLogin = this.authService.isTalisLoginUri(
          new URI(appState.target),
        );

        return {
          personaUser: this.authService.authorizePersonaUser(
            personaUser,
            isTalisLogin,
          ),
          targetUri: appState.target.toString(),
        };
      }),
      tap(({ personaUser }) => this.authService.updateUser(personaUser)),
      switchMap(({ personaUser, targetUri }) =>
        this.userService
          .loginWithTarl(personaUser.guid, personaUser.activePid)
          .pipe(
            catchError(() => of(null)),
            map(tarlUser =>
              currentPersonaUser(this.appConfig, personaUser, tarlUser),
            ),
            map(currentUser => new StoreCurrentUserAction({ currentUser })),
            tap(() => {
              this.router.navigateByUrl(targetUri.toString(), {
                replaceUrl: true,
              });
            }),
          ),
      ),
      catchError((error: unknown) => {
        const errorUri = this.authErrorService.buildUserAuthErrorUrl(error);

        this.redirectionService.redirectToExternalUrl(errorUri);

        return NEVER;
      }),
    ),
  );

  completeBreakoutLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentUserActionTypes.COMPLETE_BREAKOUT_LOGIN),
      first(),
      map(({ payload: { user, target } }: CompleteBreakoutLoginAction) => {
        const isTalisLogin = this.authService.isTalisLoginUri(new URI(target));

        return {
          personaUser: this.authService.authorizePersonaUser(
            user,
            isTalisLogin,
          ),
          targetUri: target,
        };
      }),
      tap(({ personaUser }) => this.authService.updateUser(personaUser)),
      switchMap(({ personaUser, targetUri }) =>
        this.userService
          .loginWithTarl(personaUser.guid, personaUser.activePid)
          .pipe(
            catchError(() => of(null)),
            map(tarlUser =>
              currentPersonaUser(this.appConfig, personaUser, tarlUser),
            ),
            map(currentUser => new StoreCurrentUserAction({ currentUser })),
            tap(() => {
              this.router.navigateByUrl(targetUri.toString(), {
                replaceUrl: true,
              });
            }),
          ),
      ),
      catchError((error: unknown) => {
        const errorUri = this.authErrorService.buildUserAuthErrorUrl(error);

        this.router.navigateByUrl(errorUri, { skipLocationChange: true });

        return NEVER;
      }),
    ),
  );

  completeLtiBreakoutLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentUserActionTypes.COMPLETE_LTI_BREAKOUT_LOGIN),
      first(),
      map(({ payload: { user, target } }: CompleteLtiBreakoutLoginAction) => {
        const isTalisLogin = this.authService.isTalisLoginUri(new URI(target));

        return {
          personaUser: this.authService.authorizePersonaUser(
            user,
            isTalisLogin,
          ),
          targetUri: target,
        };
      }),
      tap(({ personaUser }) => this.authService.updateUser(personaUser)),
      switchMap(({ personaUser, targetUri }) =>
        this.userService
          .loginWithTarl(personaUser.guid, personaUser.activePid)
          .pipe(
            catchError(() => of(null)),
            map(tarlUser =>
              currentPersonaUser(this.appConfig, personaUser, tarlUser),
            ),
            map(currentUser => new StoreCurrentUserAction({ currentUser })),
            tap(() => {
              this.router.navigateByUrl(targetUri.toString(), {
                replaceUrl: true,
              });
            }),
          ),
      ),
      catchError((error: unknown) => {
        const errorUri = this.authErrorService.buildUserAuthErrorUrl(error);

        this.router.navigateByUrl(errorUri, { skipLocationChange: true });

        return NEVER;
      }),
    ),
  );

  logInWithOAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserActionTypes.LOG_IN),
        tap(() => this.authService.login()),
      ),
    { dispatch: false },
  );

  logInWithOAuthDuringRouteNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserActionTypes.LOG_IN_DURING_ROUTE_NAVIGATION),
        tap((action: LogInDuringRouteNavigationAction) =>
          this.authService.loginDuringRouteNavigation(action.payload.stateUrl),
        ),
      ),
    { dispatch: false },
  );

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserActionTypes.LOG_OUT),
        tap(() => this.authService.logout()),
      ),
    { dispatch: false },
  );

  updateAccessToken$ = createEffect(() =>
    this.authService.user$.pipe(
      map(user => user.accessToken),
      distinctUntilChanged(),
      map(accessToken => new StoreAccessTokenAction({ accessToken })),
    ),
  );
}
